import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Check, AlertTriangle, Loader2, Calendar, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

export default function BookingSuccess() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    try {
      const { data } = await axios.get(
        `${API}/api/lesson-payments/checkout-status/${sessionId}`,
        { withCredentials: true }
      );
      // Surface-2 Truth Hardening (Feb 2026): success is gated on the
      // authoritative `paid_at` timestamp (Stripe webhook / manual
      // mark-paid). `payment_status === "paid"` alone is NOT
      // sufficient authority. The backend flips both in the same
      // `_fulfill_lesson_payment` write, so this is a zero-behaviour
      // hardening — we simply refuse to render "You're booked in"
      // wording until the authoritative timestamp lands.
      if (data.paid_at) {
        setStatus("success");
        setBooking(data);
        return;
      }
      if (data.status === "expired") {
        setStatus("expired");
        return;
      }
      // Still processing — retry
      if (attempts < 8) {
        setAttempts((a) => a + 1);
      } else {
        setStatus("timeout");
      }
    } catch {
      if (attempts < 8) {
        setAttempts((a) => a + 1);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status === "checking" && sessionId) {
      const timer = setTimeout(pollStatus, attempts === 0 ? 500 : 2000);
      return () => clearTimeout(timer);
    }
  }, [status, attempts, pollStatus, sessionId]);

  if (status === "checking") {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center px-4">
        <div data-testid="payment-processing" className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
          <h1 className="text-xl font-extrabold text-slate-900 dark:text-white">
            Locking in your lesson...
          </h1>
          <p className="text-sm text-slate-500">Usually done in a few seconds</p>
        </div>
      </div>
    );
  }

  if (status === "success" && booking) {
    const dateObj = new Date(booking.date + "T00:00:00");
    const dateStr = dateObj.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const durLabel =
      booking.duration === 60
        ? "1 Hour"
        : booking.duration === 90
        ? "90 Minutes"
        : "2 Hours";

    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center px-4">
        <div data-testid="booking-confirmed" className="max-w-sm w-full space-y-6 text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">
              You're booked in
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Paid and in the diary — see you then
            </p>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 space-y-3 text-left">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm font-bold text-slate-900 dark:text-white">{dateStr}</p>
                <p className="text-xs text-slate-400">
                  {fmtTime(booking.start_time)} — {durLabel}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-emerald-500" />
              <p className="text-sm font-bold text-emerald-600">
                £{Number(booking.amount).toFixed(2)} paid
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-400 leading-relaxed">
            Plans change — we get it. Cancel free up to 24 hours before.
            After that, it may not be refundable.
          </p>
          <div className="space-y-2">
            <Button
              data-testid="back-to-booking"
              onClick={() => nav("/learn/book")}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl"
            >
              Book another
            </Button>
            <Button
              data-testid="go-home"
              onClick={() => nav("/learn")}
              variant="outline"
              className="w-full h-12 rounded-2xl"
            >
              Back home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Error / expired / timeout states
  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center px-4">
      <div data-testid="payment-failed" className="max-w-sm w-full space-y-6 text-center">
        <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-10 h-10 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">
            {status === "expired" ? "That session timed out" : "Payment didn't go through"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {status === "expired"
              ? "No charge — just start again when you're ready."
              : status === "timeout"
              ? "Still processing. Check your bookings in a moment."
              : "Something tripped up on the way. Let's try again."}
          </p>
        </div>
        <Button
          data-testid="retry-booking"
          onClick={() => nav("/learn/book")}
          className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl"
        >
          Try again
        </Button>
      </div>
    </div>
  );
}
